import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { Money } from '@bpos';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';

import { ReBetTrackingService } from './re-bet-tracking.service';
import { ReBetAvailabilityService } from './re-bet-visibility.service';

@Component({
    selector: 'ms-re-bet-button',
    templateUrl: 're-bet-button.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReBetButtonComponent implements OnChanges {
    @Input() money: Money;
    @Input() possibleWinnings: number;
    @Input() betAmount: number;
    @Input() listType: BetslipListType;
    @Output() clickAction = new EventEmitter<void>();

    constructor(
        private siteCore: Sitecore,
        private reBetAvailabilityService: ReBetAvailabilityService,
        private moneyService: MoneyService,
        private trackingService: ReBetTrackingService,
    ) {}

    ngOnChanges(changes: ISimpleChanges<ReBetButtonComponent>): void {
        if (changes.betAmount?.firstChange) {
            this.trackingService.trackCtaShown(this.betAmount, this.listType);
        }
    }

    onClick(): void {
        this.clickAction.next();
        this.trackingService.trackCtaClicked(this.betAmount, this.listType);
    }

    get title(): string {
        return interpolateDoubleBracesString(
            this.listType === BetslipListType.Open
                ? this.siteCore.reBet.messages.CtaTitleForOpenTab
                : this.siteCore.reBet.messages.CtaTitleForSettledTab,
            { number: this.betAmount },
        );
    }

    get subTitle(): string {
        return interpolateDoubleBracesString(
            this.listType === BetslipListType.Open
                ? this.siteCore.reBet.messages.CtaSubTitleForOpenTab
                : this.siteCore.reBet.messages.CtaSubTitleForSettledTab,
            {
                stake: this.getCurrencyValue(this.money.value, this.money.currency),
                possibleWinnings: `<span class="winnings">${this.getCurrencyValue(
                    this.moneyService.roundMoney(this.possibleWinnings),
                    this.money.currency,
                )}</span>`,
            },
        );
    }

    get subTitleEnabled(): boolean {
        return this.reBetAvailabilityService.isCTASubTextAvailable(this.listType) && !!this.subTitle;
    }

    get isOpenTab(): boolean {
        return this.listType === BetslipListType.Open;
    }

    private getCurrencyValue(value: number | string, currency: string): string {
        return this.moneyService.transform(value, true, 2, false, undefined, currency);
    }
}
